/* eslint sort-keys: 0 */
import { SortDirections } from "@app/shared/waffles";
import { MemberMapResults } from "@app/projects/results/common/mapping";
import ServiceBase from "@app/shared/serviceBase";
import { FilterCriteriaModel } from "@app/shared/FilterCriteria";
import { querystringify } from "@app/shared/utility/querystringify";
import { MemberSetCargoColumn } from "@app/members/interfaces";
import { MemberSetModel, StateMemberCountModel, CountyMemberCountModel, MemberModel, FilteredMemberSetModel, SaveFilteredResourceModel, MemberSetModelWithActiveProjects } from "@app/members/memberSetModels";

/** Manages interaction with the member set API. */
export default class MemberSetService extends ServiceBase {
  /** Retrieves all member sets. */
  public async get(includeShared?: boolean, includeVersions?: boolean, activatedOnly?: boolean, includeArchivedVersions?: boolean, isFilterable?: boolean): Promise<MemberSetModel[]> {
    return await this.makeGet<MemberSetModel[]>("membersets", { activatedOnly, includeArchivedVersions, includeShared, includeVersions, isFilterable });
  }

  /** Retrieves all active member sets. */
  public async getActive(includeShared?: boolean, includeVersions?: boolean, activatedOnly?: boolean, includeArchivedVersions?: boolean, isFilterable?: boolean): Promise<MemberSetModel[]> {
    return await this.makeGet<MemberSetModel[]>("membersets/active", { activatedOnly, includeArchivedVersions, includeShared, includeVersions, isFilterable });
  }

  /** Retrieves member sets depending on role. */
  public async getOnRole(includeShared?: boolean, includeVersions?: boolean, activatedOnly?: boolean, includeArchivedVersions?: boolean, isFilterable?: boolean): Promise<MemberSetModel[]> {
    return await this.makeGet<MemberSetModel[]>("membersets/roleBased", { activatedOnly, includeArchivedVersions, includeShared, includeVersions, isFilterable });
  }

  /** Retrieves member sets depending on role. And Optionally filtered by name, description, resource status, resource type */
  public async getMemberSetsByCriteria( includeShared: boolean = true, includeVersions: boolean = true, activatedOnly: boolean = false, includeArchivedVersions: boolean = true, isFilterable: boolean = false, nameFilter?: string, descriptionFilter?: string, typeFilter?: string, statusFilter?: string ): Promise<MemberSetModel[]> {
    const params = { activatedOnly, desc: descriptionFilter || undefined, includeArchivedVersions, includeShared, includeVersions, isFilterable, name: nameFilter || undefined, status: statusFilter || undefined, type: typeFilter || undefined };

    const url = `membersets/roleBased/search?${querystringify(params)}`;
    return await this.makeGet<MemberSetModel[]>(url);
  }


  /** Retrieves filtered member sets for the given source member set ID. */
  public async getFilteredMemberSets(sourceMemberSetId: number): Promise<MemberSetModel[]> {
    return await this.makeGet<MemberSetModel[]>(`membersets/${sourceMemberSetId}/filtered`);
  }

  /**
   * Retrieves a member set corresponding to a given identifier.
   * @param id The member set's identifier.
   */
  public async getById(id: number, activatedOnly?: boolean, includeArchivedVersions?: boolean): Promise<MemberSetModel> {
    return await this.makeGet<MemberSetModel>(`membersets/${id}`, { activatedOnly, includeArchivedVersions });
  }

  /**
   * Creates a new member set with a given name and description.
   * @param name The name of the member set.
   * @param description The description of the member set.
   */
  public async create(name: string, description: string, isPrivate: boolean): Promise<void> {
    //TODO: This does not pass the correct body.
    await this.makePost("membersets", { description, isPrivate, name });
  }

  /**
   * activates a specified membersetVersion for a specified memberSetId
   * @param memberSetId The member set's identifier.
   * @param memberSetVersionId The id of the memberset version
   * @param filteredMemberSetIds IDs of filtered membersets to activate. An empty array activates none of them.
   */
  public async activateMemberSetVersion(memberSetId: number, memberSetVersionId: number, filteredMemberSetIds: number[]): Promise<number> {
    const query = {
      filteredMemberSetIds,
      hasExplicitFilteredSelection: true,
    };

    return await this.makePut(`membersets/${memberSetId}/versions/${memberSetVersionId}/activate?${querystringify(query)}`);
  }

  /**
   * Updates the status of the memberSet
   * @param resourceId
   */
  public async archiveMemberSet(resourceId: number): Promise<string> {
    return await this.makePut<string>(`membersets/${resourceId}/archive`);
  }

  /**
   * Updates the status of the memberSet
   * @param resourceId
   */
  public async archiveMemberSetAndMemberSetVersions(resourceIds: number[]): Promise<string> {
    return await this.makePut<string>(`membersets/archive-member-set-and-versions`, resourceIds);
  }

  /**
   * Updates the status of the memberSet
   * @param resourceId
   */
  public async activateMemberSetAndMemberSetVersions(resourceId: number): Promise<string> {
    return await this.makePut<string>(`membersets/${resourceId}/activate-member-set-and-versions`);
  }

  /**
   * gets member count for a memberset
   * @param memberSetId The member set's identifier.
   * @param memberSetVersionId The id of the memberset version
   */
  public async getMemberCount(memberSetId: number, memberSetVersionId: number): Promise<number> {
    return await this.makeGet(`membersets/${memberSetId}/versions/${memberSetVersionId}/count`);
  }

  /**
   * gets member count by state for a memberset
   * @param memberSetId The member set's identifier.
   * @param memberSetVersionId The id of the memberset version
   */
  public async getMemberCountByState(memberSetId: number, memberSetVersionId: number): Promise<StateMemberCountModel[]> {
    return await this.makeGet(`membersets/${memberSetId}/versions/${memberSetVersionId}/countbystate`);
  }

  /**
   * gets member count by county for a memberset
   * @param memberSetId The member set's identifier.
   * @param memberSetVersionId The id of the memberset version
   */
  public async getMemberCountByCounty(memberSetId: number, memberSetVersionId: number): Promise<CountyMemberCountModel[]> {
    return await this.makeGet(`membersets/${memberSetId}/versions/${memberSetVersionId}/countbycounty`);
  }

  /**
   * archives a specified membersetVersion for a specified memberSetId
   * @param memberSetId The member set's identifier.
   * @param memberSetVersionId The id of the memberset version
   */
  public async archiveMemberSetVersion(memberSetId: number, memberSetVersionId: number): Promise<number> {
    return await this.makePut(`membersets/${memberSetId}/versions/${memberSetVersionId}/archive`);
  }

  /**
   * Gets the cargo columns for a given member set
   * @param memberSetId The member set's identifier.
   */
  public async getMemberSetSchema(memberSetId: number): Promise<MemberSetCargoColumn[]> {
    return await this.makeGet<MemberSetCargoColumn[]>(`membersets/${memberSetId}/schema`);
  }

  public async getSavedMemberSetFilters(memberSetId: number, versionId?: number): Promise<FilterCriteriaModel[]> {
    const versionParam = versionId !== undefined && versionId !== null ? `?versionId=${versionId}` : "";
    return await this.makePost<FilterCriteriaModel[]>(`membersets/${memberSetId}/filters${versionParam}`);
  }

  public async getMemberSetMembers(
    memberSetId: number,
    memberSetVersionId: number,
    pageSize: number,
    pageNumber: number,
    sortDirection?: SortDirections,
    sortedColumn?: string,
  ): Promise<MemberModel[]> {
    return await this.makeGet<MemberModel[]>(`membersets/${memberSetId}/versions/${memberSetVersionId}/members`, { pageNumber, pageSize, sortDirection, sortedColumn });
  }

  public async createFilteredMemberSet(sourceResourceId: number, name: string, description: string, isPrivate: boolean, filters?: FilterCriteriaModel[]): Promise<MemberSetModel> {
    const model: FilteredMemberSetModel = {
      description,
      isPrivate,
      name,
      sourceResourceId,
      uiFilters: filters,
    };

    return await this.makePost<MemberSetModel>(`membersets/filtered`, model);
  }

  public async GetMemberResultsAsync(
    customProjectId: number,
    projectRunConfigurationId: number,
    regionSetId: string,
    specialtyGroupCode: string
  ): Promise<MemberMapResults[]> {
    const url = `/projects/${customProjectId}/runconfigurations/${projectRunConfigurationId}/regionsets/${regionSetId}/memberresults?specialtyGroupCode=${encodeURIComponent(specialtyGroupCode)}`;

    return await this.makeGet<MemberMapResults[]>(url);
  }

  public async GetMemberResultsMultiRegionAsync(
    customProjectId: number,
    projectRunConfigurationId: number,
    regionSetIds: string[],
    specialtyGroupCode: string
  ): Promise<MemberMapResults[]> {
    const requestQuery = {
      regionSetIds,
      specialtyGroupCode
    };
    const url = `/projects/${customProjectId}/runconfigurations/${projectRunConfigurationId}/memberresults-multiregion`;

    return await this.makeGet<MemberMapResults[]>(url, requestQuery);
  }

  public async saveFilterCriteria(
    filteredResourceId: number,
    filteredSetVersionId: number,
    sourceSetVersionId: number,
    sourceMemberSetId: number,
    filters?: FilterCriteriaModel[],
  ): Promise<MemberSetModel> {
    const model: SaveFilteredResourceModel = {
      criteria: filters,
      filteredResourceId,
      filteredSetVersionId,
      sourceMemberSetId,
      sourceSetVersionId,
    };

    return await this.makePatch<MemberSetModel>(`membersets/filtered`, model);
  }

  public async GetEditTemplateOptionsMemberSetData(
    includeArchived: boolean,
    includeArchivedVersions: boolean,
    includeOnlyClientAssigned: boolean,
    universalOnly: boolean
  ): Promise<MemberSetModelWithActiveProjects[]> {
    const query = {
      includeArchived,
      includeArchivedVersions,
      includeOnlyClientAssigned,
      universalOnly
    };
    return await this.makeGet<MemberSetModelWithActiveProjects[]>(`/membersets/GetEditTemplateOptionsMemberSetData?${querystringify(query)}`);
  }
}
